import { useEffect, useState, useRef } from "react";
import { EyeOff, Send, SkipForward, Check, Infinity as Inf, Trophy, LogOut, RotateCcw, User, HelpCircle, Search, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useT } from "../lib/i18n";
import { LegalFooter } from "./LegalFooter";

const safeArray = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  return Object.values(val);
};

export default function Game({ room, playerId, actions }) {
  const { t } = useT();
  const isHost = playerId === room?.host_id;
  
  const safePlayers = safeArray(room?.players);
  const activeTurnIndex = room?.active_turn_index || 0;
  const activePlayer = safePlayers[activeTurnIndex];
  const isMyTurn = activePlayer?.id === playerId;
  
  const playerCards = room?.player_cards || {};
  const myActiveCard = playerCards[activePlayer?.id];
  const selectionPhase = room?.card_selection;
  const isSelecting = selectionPhase?.target === activePlayer?.id;
  
  const turnStartTime = room?.turn_start_time || Date.now() / 1000;
  const [now, setNow] = useState(Date.now() / 1000);
  
  const [isProcessing, setIsProcessing] = useState(false);
  
  // CZAT NA ŻYWO
  const [chatMsg, setChatMsg] = useState("");
  const chatEndRef = useRef(null);
  const chatMessages = safeArray(room?.chat);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages.length]);

  const sendChatMessage = (e) => {
    e.preventDefault();
    if (!chatMsg.trim()) return;
    const newMsg = {
      id: Date.now() + Math.random(),
      senderId: playerId,
      senderName: safePlayers.find(p => p.id === playerId)?.name || "?",
      color: safePlayers.find(p => p.id === playerId)?.color || "#fff",
      text: chatMsg.trim()
    };
    const updatedChat = [...chatMessages, newMsg].slice(-50);
    if (actions.config) actions.config({ chat: updatedChat });
    else if (actions.updateRoom) actions.updateRoom({ ...room, chat: updatedChat });
    setChatMsg("");
  };

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now() / 1000), 250);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
      setIsProcessing(false);
  }, [activeTurnIndex, room?.card_index]);
  
  const elapsed = Math.max(0, now - turnStartTime);
  const remaining = room?.infinite ? null : Math.max(0, Math.ceil((room?.turn_seconds || 60) - (isSelecting ? 0 : elapsed)));
  
  const maxSkips = Math.ceil((room?.cards_per_player || 3) / 2);
  const currentSkips = activePlayer?.skips || 0;
  const canSkip = currentSkips < maxSkips;

  const eligibleVoters = safePlayers.filter(p => p.id !== activePlayer?.id);
  const missingVoters = eligibleVoters.filter(p => !safeArray(room?.confirm_votes).includes(p.id));
  
  useEffect(() => {
      if (!isHost) return;
      
      if (activePlayer && !playerCards[activePlayer.id] && !isSelecting) {
          const cIdx = room?.card_index || 0;
          const currentDeck = safeArray(room?.deck);
          const dLen = currentDeck.length > 0 ? currentDeck.length : 1;
          const options = [];
          
          for(let i=0; i<3; i++) {
              if (currentDeck.length > 0) options.push(currentDeck[(cIdx + i) % dLen]);
          }
          
          const updates = {
              card_selection: { target: activePlayer.id, options, votes: {} },
              card_index: (cIdx + 3) % dLen,
              turn_start_time: Date.now() / 1000
          };
          if (actions.config) actions.config(updates);
          else if (actions.updateRoom) actions.updateRoom({ ...room, ...updates });
      }
      
      if (isSelecting && selectionPhase) {
          const votes = selectionPhase.votes || {};
          const votersCount = safePlayers.length > 1 ? safePlayers.length - 1 : 0;
          
          if (Object.keys(votes).length >= votersCount) {
              let maxOption = 0;
              if (votersCount > 0) {
                  const counts = {};
                  let maxVotes = -1;
                  Object.values(votes).forEach(v => { counts[v] = (counts[v] || 0) + 1; });
                  Object.keys(counts).forEach(optIdx => {
                      if (counts[optIdx] > maxVotes) {
                          maxVotes = counts[optIdx];
                          maxOption = parseInt(optIdx);
                      }
                  });
              }
              const winningCard = safeArray(selectionPhase?.options)?.[maxOption] || {text: "Błąd z kartą"};
              const updates = {
                  player_cards: { ...playerCards, [activePlayer.id]: winningCard },
                  card_selection: null,
                  turn_start_time: Date.now() / 1000
              };
              if (actions.config) actions.config(updates);
              else if (actions.updateRoom) actions.updateRoom({ ...room, ...updates });
          }
      }
  }, [isHost, activePlayer, playerCards, isSelecting, room, actions, selectionPhase, safePlayers.length]);

  const endTurn = (clearCard = false, customPlayersArray = null) => {
      const playersCount = safePlayers.length || 1;
      const nextIndex = (activeTurnIndex + 1) % playersCount;
      
      const updates = {
          active_turn_index: nextIndex,
          turn_start_time: Date.now() / 1000,
          confirm_votes: [],
          question_votes: [] 
      };
      
      if (customPlayersArray) updates.players = customPlayersArray;
      if (clearCard && activePlayer) updates.player_cards = { ...playerCards, [activePlayer.id]: null };

      if (actions.config) actions.config(updates);
      else if (actions.updateRoom) actions.updateRoom({ ...room, ...updates });
  };

  const handleLeave = async () => {
      const updatedPlayers = safePlayers.filter(p => p.id !== playerId);
      if (actions.updateRoom) await actions.updateRoom({ ...room, players: updatedPlayers });
      if (actions.leave) actions.leave();
  };

  const reqVotes = Math.max(1, safePlayers.length - 1);
  const qVotes = safeArray(room?.question_votes);
  const hasVotedQ = qVotes.includes(playerId);
  const hasVoted = safeArray(room?.confirm_votes).includes(playerId);

  const handleQuestion = () => {
      if (isProcessing || hasVotedQ) return;
      const newQVotes = [...qVotes, playerId];
      if (actions.config) actions.config({ question_votes: newQVotes });
      else if (actions.updateRoom) actions.updateRoom({ ...room, question_votes: newQVotes });
  };

  useEffect(() => {
      if (!isHost || isSelecting) return;
      const currentQVotes = safeArray(room?.question_votes);
      if (currentQVotes.length >= reqVotes && !isProcessing) {
          setIsProcessing(true);
          const timeTaken = Math.max(0, (Date.now() / 1000) - turnStartTime);
          const updatedPlayers = safePlayers.map(p => 
              p.id === activePlayer?.id ? { 
                  ...p, 
                  moves: (p.moves || 0) + 1,
                  total_time: (p.total_time || 0) + timeTaken
              } : p
          );
          endTurn(false, updatedPlayers);
      }
  }, [isHost, room?.question_votes, reqVotes, isProcessing, activePlayer?.id, turnStartTime, isSelecting]);

  const voteForCard = (index) => {
      const currentVotes = selectionPhase?.votes || {};
      if (currentVotes[playerId] === index) return;
      const newVotes = { ...currentVotes, [playerId]: index };
      if (actions.config) actions.config({ card_selection: { ...selectionPhase, votes: newVotes } });
      else if (actions.updateRoom) actions.updateRoom({ ...room, card_selection: { ...selectionPhase, votes: newVotes } });
  };

  const processedTurnTimeRef = useRef(null);
  useEffect(() => {
    if (isHost && !room?.infinite && remaining !== null && remaining <= 0 && !isSelecting) {
       if (processedTurnTimeRef.current !== turnStartTime) {
           processedTurnTimeRef.current = turnStartTime;
           const timeTaken = Math.max(0, (Date.now() / 1000) - turnStartTime);
           const updatedPlayers = safePlayers.map(p => 
               p.id === activePlayer?.id ? { 
                   ...p, 
                   moves: (p.moves || 0) + 1,
                   total_time: (p.total_time || 0) + timeTaken
               } : p
           );
           endTurn(false, updatedPlayers); 
       }
    }
  }, [remaining, isHost, room, activeTurnIndex, isSelecting, actions, activePlayer?.id, turnStartTime]);
  
  const [guess, setGuess] = useState("");

  const getCardText = (card) => {
    if (!card) return "—";
    let rawLang = localStorage.getItem('language') || localStorage.getItem('lang') || 'pl';
    const currentLang = String(rawLang).split('-')[0].toLowerCase();
    
    if (card[currentLang]) return card[currentLang];
    if (card.en) return card.en;
    if (card.pl) return card.pl;
    if (card.text) {
       const key = String(card.text).toLowerCase();
       const translated = t(key);
       return translated !== key ? translated : card.text;
    }
    if (typeof card === 'string') return card;
    return "—";
  };

  const submitGuess = (e) => {
      e.preventDefault();
      if (!guess.trim() || !myActiveCard || isProcessing) return;
      setIsProcessing(true);
      
      const timeTaken = Math.max(0, (Date.now() / 1000) - turnStartTime);
      const updatedPlayers = safePlayers.map(p => 
          p.id === playerId ? { 
              ...p, 
              moves: (p.moves || 0) + 1,
              total_time: (p.total_time || 0) + timeTaken
          } : p
      );
      
      const expectedTrans = String(getCardText(myActiveCard)).toLowerCase().trim();
      const expectedOrig = String(myActiveCard?.en || myActiveCard?.pl || myActiveCard?.text || "").toLowerCase().trim();
      const g = guess.toLowerCase().trim();
      
      if (g === expectedTrans || g === expectedOrig) {
          toast.success(t("correct", { n: 1 }) || "Zgadłeś! +1 pkt 🎉");
          if (actions.guess) actions.guess(); 
          setTimeout(() => endTurn(true, updatedPlayers), 600); 
      } else {
          toast.error(t("wrong") || "Nie do końca, tura przepada!");
          endTurn(false, updatedPlayers); 
      }
      setGuess("");
  };

  if (room?.status === "ended") {
    const ranked = [...safePlayers].sort((a, b) => {
        if (room?.mode === "fcfs") {
            const avgA = a.moves ? (a.total_time || 0) / a.moves : 999;
            const avgB = b.moves ? (b.total_time || 0) / b.moves : 999;
            return avgA - avgB;
        } else {
            if (b.score !== a.score) return (b.score || 0) - (a.score || 0);
            return (a.moves || 0) - (b.moves || 0);
        }
    });

    return (
      <div className="grain min-h-screen flex flex-col bg-[#050510]">
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <h1 className="text-4xl font-black mb-8 text-[#00FFFF]">{t("game_over")}</h1>
          <div className="w-full max-w-sm space-y-3 mb-8">
              {ranked.map((p, i) => {
                  const avgTime = p.moves && p.moves > 0 ? ((p.total_time || 0) / p.moves).toFixed(1) : "0.0";
                  return (
                    <div key={p.id} className="p-4 bg-[#12121A] rounded-xl flex items-center justify-between border-2 border-white/10">
                        <div className="flex items-center gap-3">
                            <span className={`font-bold ${i === 0 ? "text-[#FFE800]" : "text-[#A0A0B0]"}`}>{i+1}.</span>
                            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color || "#fff" }} />
                            <div className="flex flex-col">
                               <span className="font-bold text-white">{p.name || "?"}</span>
                               <span className="text-[10px] text-[#A0A0B0] uppercase tracking-widest mt-0.5">
                                  ⚡ {p.moves || 0} ruchów
                               </span>
                            </div>
                        </div>
                        <div className="flex flex-col items-end">
                           <span className="font-black text-[#FFE800] text-xl leading-none">
                              {room?.mode === "fcfs" ? `${avgTime}s` : (p.score || 0)}
                           </span>
                        </div>
                    </div>
                  );
              })}
          </div>
          {isHost && (
              <button onClick={async () => {
                  if (actions.config) await actions.config({ card_index: Math.floor(Math.random() * 1000) });
                  actions.restart();
              }} className="bg-[#FF007F] px-8 py-4 rounded-2xl font-bold flex items-center gap-2 hover:-translate-y-1 transition-transform">
                  <RotateCcw size={20}/> {t("play_again")}
              </button>
          )}
        </div>
        <div className="w-full z-20 relative"><LegalFooter screen="WYNIKI" /></div>
      </div>
    );
  }

  const timeColor = room?.infinite ? "#00FFFF" : remaining <= 10 ? "#FF007F" : remaining <= 20 ? "#FFE800" : "#00FF66";

  return (
    <div className="grain relative min-h-screen flex flex-col">
      <div className="flex-1 w-full max-w-6xl mx-auto px-4 py-6 sm:p-8 relative z-10">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-black text-lg">HEADS UP</span>
            <span className="text-xs font-mono2 tracking-widest px-2 py-1 rounded-full bg-[#FF007F]/20 text-[#FF007F] uppercase">
              {room?.mode === "podium" ? t("podium_tag") : room?.mode === "ofa" ? "Kooperacja" : t("fcfs_tag")}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 font-display font-black text-2xl" style={{ color: timeColor }}>
              {room?.infinite ? <Inf size={22}/> : (isSelecting ? "⏳" : `${remaining}s`)}
            </div>
            <button onClick={handleLeave} className="flex items-center gap-1.5 px-3 py-2 rounded-xl border-2 border-white/15 text-[#A0A0B0] hover:border-[#FF007F] hover:text-[#FF007F] transition-colors font-bold text-xs">
              <LogOut size={16}/> {t("leave")}
            </button>
          </div>
        </div>

        <div className={`p-4 mb-6 rounded-2xl border-2 flex items-center justify-between ${isMyTurn ? 'bg-[#FF007F]/10 border-[#FF007F]' : 'bg-[#00FFFF]/10 border-[#00FFFF]'}`}>
           <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center font-display font-black text-[#050510] text-xl" style={{ backgroundColor: activePlayer?.color || "#fff" }}>
                 {(activePlayer?.name || "?").charAt(0).toUpperCase()}
              </div>
              <div>
                 <div className="text-xs uppercase tracking-widest text-[#A0A0B0] font-mono2 mb-1">Obecna tura</div>
                 <div className="font-display font-bold text-2xl text-white">
                     {isMyTurn ? t("your_turn") : t("turn_of", { name: activePlayer?.name || "?" })}
                 </div>
              </div>
           </div>
           
           <div className="flex items-center gap-3">
               {!isMyTurn && (
                  <button 
                     onClick={handleQuestion}
                     disabled={hasVotedQ}
                     className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-colors z-20 relative ${hasVotedQ ? "bg-[#00FFFF]/20 text-[#00FFFF] border border-[#00FFFF]/50" : "bg-[#00FFFF]/10 text-[#00FFFF] border border-[#00FFFF]/30 hover:bg-[#00FFFF]/20"}`}
                  >
                     <HelpCircle size={16} />
                     <span className="hidden sm:inline">{hasVotedQ ? t("voted") || "Zagłosowano" : t("asked_question") || "Zadał pytanie"}</span>
                     <span className="sm:hidden">{hasVotedQ ? "✓" : "+ Pytanie"}</span>
                     <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-black ml-1 ${hasVotedQ ? "bg-[#00FFFF]/30" : "bg-black/20"}`}>
                        {qVotes.length} / {reqVotes}
                     </span>
                  </button>
               )}
               {isMyTurn && <User size={28} className="text-[#FF007F]" />}
           </div>
        </div>

        <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6">
           <div className="p-6 sm:p-10 rounded-3xl bg-[#12121A] border-2 border-[#00FFFF]/30 flex flex-col items-center justify-center min-h-[350px] relative overflow-hidden pop-in">
               
               {isSelecting ? (
                   isMyTurn ? (
                       <div className="text-center w-full flex flex-col items-center">
                           <EyeOff className="text-[#505060] mb-4" size={60}/>
                           <div className="font-display text-2xl font-black text-[#FFE800] mb-2">Losowanie...</div>
                           <p className="text-[#A0A0B0] font-bold">Pozostali gracze wybierają dla Ciebie kartę!</p>
                       </div>
                   ) : (
                       <div className="text-center w-full">
                           <h3 className="font-display text-2xl font-black text-[#00FFFF] mb-6">Wybierz kartę dla {activePlayer?.name}</h3>
                           <div className="flex flex-col gap-3 max-w-sm mx-auto">
                               {safeArray(selectionPhase?.options).map((opt, idx) => {
                                   const votesCount = Object.values(selectionPhase.votes || {}).filter(v => v === idx).length;
                                   const isMyVote = selectionPhase.votes?.[playerId] === idx;
                                   return (
                                       <button 
                                           key={idx}
                                           onClick={() => voteForCard(idx)}
                                           className={`relative p-4 rounded-xl border-2 font-bold text-lg transition-all ${isMyVote ? "border-[#FFE800] bg-[#FFE800]/20 text-[#FFE800]" : "border-white/10 bg-[#12121A] text-white hover:border-white/30"}`}
                                       >
                                           {getCardText(opt)}
                                           {votesCount > 0 && (
                                               <span className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 text-[#FFE800] px-2 py-1 rounded-md text-xs">
                                                   {votesCount} głos
                                               </span>
                                           )}
                                       </button>
                                   );
                               })}
                           </div>
                       </div>
                   )
               ) : (
                   isMyTurn ? (
                       <div className="text-center w-full flex flex-col items-center">
                           <EyeOff className="text-[#505060] mb-4" size={60}/>
                           <div className="font-display text-2xl font-black text-[#505060] mb-2">{t("hidden_ask")}</div>
                           <div className="text-[#A0A0B0] font-bold mb-6 tracking-widest uppercase text-xs">
                              Ruchy: <span className="text-[#FF007F] text-sm">{activePlayer?.moves || 0}</span>
                           </div>
                           
                           <form onSubmit={submitGuess} className="w-full max-w-sm flex gap-2 mb-4">
                              <input value={guess} onChange={(e) => setGuess(e.target.value)} placeholder={t("type_guess")} className="flex-1 bg-[#12121A] border-2 border-white/10 rounded-xl py-3 px-4 focus:outline-none focus:border-[#FFE800]" />
                              <button type="submit" className="px-5 rounded-xl bg-[#FFE800] text-[#050510] font-bold"><Send size={20}/></button>
                           </form>
                           
                           <button 
                              onClick={() => { 
                                if (isProcessing || !canSkip) return;
                                setIsProcessing(true);
                                
                                if (actions.skip) actions.skip(); 
                                
                                const timeTaken = Math.max(0, (Date.now() / 1000) - turnStartTime);
                                const updatedPlayers = safePlayers.map(p => 
                                    p.id === playerId ? { 
                                        ...p, 
                                        moves: (p.moves || 0) + 1,
                                        total_time: (p.total_time || 0) + timeTaken,
                                        skips: (p.skips || 0) + 1
                                    } : p
                                );

                                setTimeout(() => endTurn(true, updatedPlayers), 400); 
                              }} 
                              disabled={!canSkip}
                              className={`w-full max-w-sm flex items-center justify-center gap-2 py-3.5 rounded-xl border-2 font-bold transition-colors ${canSkip ? "border-[#FF007F]/50 text-[#FF007F] hover:bg-[#FF007F]/10" : "border-white/10 text-white/30 bg-white/5 cursor-not-allowed"}`}
                           >
                              <SkipForward size={18}/> {t("skip_card")} ({currentSkips}/{maxSkips})
                           </button>
                       </div>
                   ) : (
                       <div className="text-center w-full flex flex-col items-center">
                           <div className="font-display text-4xl sm:text-6xl font-black text-[#FFE800] drop-shadow-[0_0_20px_rgba(255,232,0,0.4)]">
                               {myActiveCard ? getCardText(myActiveCard) : "—"}
                           </div>
                           
                           {myActiveCard && (
                               <a 
                                 href={`https://www.google.com/search?q=${encodeURIComponent(getCardText(myActiveCard))}`}
                                 target="_blank"
                                 rel="noreferrer"
                                 className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-xl text-sm font-bold text-[#A0A0B0] hover:text-white transition-colors"
                               >
                                 <Search size={16}/> Sprawdź w Google
                               </a>
                           )}
                           
                           <button 
                               onClick={() => actions.confirm()}
                               disabled={hasVoted || !myActiveCard}
                               className={`mt-10 px-8 py-4 rounded-2xl font-bold flex items-center justify-center gap-3 w-full max-w-sm mx-auto transition-all duration-300 ${hasVoted ? "bg-[#00FF66]/20 text-[#00FF66] border-2 border-[#00FF66]/50" : "bg-[#00FF66] text-[#050510] hover:-translate-y-1 hard-shadow-white"}`}
                           >
                               <Check size={24}/> {hasVoted ? t("voted") : t("got_it")}
                               <span className={`px-2 py-1 rounded-lg text-xs font-black ml-2 ${hasVoted ? "bg-[#00FF66]/20" : "bg-black/20"}`}>
                                   {safeArray(room?.confirm_votes).length} / {reqVotes}
                               </span>
                           </button>
                           <p className="mt-4 text-[#A0A0B0] text-xs uppercase font-mono2 tracking-widest">{t("help_friends")}</p>
                       </div>
                   )
               )}
           </div>
           
           <div className="space-y-4">
               {/* CZAT NA ŻYWO */}
               <div className="p-4 rounded-2xl bg-[#12121A] border-2 border-white/10 flex flex-col h-[280px]">
                   <div className="flex items-center gap-2 text-[#A0A0B0] mb-3 font-mono2 text-xs tracking-widest uppercase">
                       <MessageSquare size={16}/> Czat (Podpowiedzi)
                   </div>
                   <div className="flex-1 overflow-y-auto custom-scrollbar flex flex-col gap-2 mb-3 pr-2">
                       {chatMessages.length === 0 ? (
                           <div className="text-[#505060] text-xs text-center my-auto">Brak wiadomości. Zacznij podpowiadać!</div>
                       ) : (
                           chatMessages.map(msg => (
                               <div key={msg.id} className="text-sm leading-tight bg-black/30 p-2 rounded-lg">
                                   <span className="font-bold" style={{ color: msg.color }}>{msg.senderName}: </span>
                                   <span className="text-white/90 break-words">{msg.text}</span>
                               </div>
                           ))
                       )}
                       <div ref={chatEndRef} />
                   </div>
                   <form onSubmit={sendChatMessage} className="flex gap-2">
                       <input 
                           value={chatMsg} 
                           onChange={(e) => setChatMsg(e.target.value)} 
                           placeholder="Napisz podpowiedź..." 
                           className="flex-1 bg-black/50 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#00FFFF] text-white" 
                       />
                       <button type="submit" disabled={!chatMsg.trim()} className="px-3 bg-[#00FFFF] text-[#050510] rounded-xl font-bold disabled:opacity-50">
                           <Send size={16}/>
                       </button>
                   </form>
               </div>
               
               <div className="p-4 rounded-2xl bg-[#12121A] border-2 border-white/10">
                 <span className="font-mono2 text-xs tracking-[0.2em] uppercase text-[#A0A0B0]">{t("scores")}</span>
                 <div className="mt-2 space-y-2">
                   {safePlayers.map((p) => (
                     <div key={p.id} className="flex items-center gap-2 text-sm">
                       <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color || "#fff" }} />
                       <span className={`font-semibold ${p.id === activePlayer?.id ? "text-white" : "text-[#A0A0B0]"}`}>{p.name || "?"}</span>
                       <span className="ml-auto font-display font-black text-lg" style={{ color: p.color || "#fff" }}>{p.score || 0}</span>
                     </div>
                   ))}
                 </div>
               </div>

               {!isSelecting && myActiveCard && !isMyTurn && (
                 <div className="p-4 rounded-2xl bg-[#12121A] border-2 border-[#FF007F]/30 shadow-[0_0_15px_rgba(255,0,127,0.1)] pop-in">
                   <span className="font-mono2 text-xs tracking-[0.2em] uppercase text-[#FF007F]">
                     Oczekujący na głos ({safeArray(room?.confirm_votes).length}/{reqVotes}):
                   </span>
                   <div className="mt-3 flex flex-wrap gap-2">
                     {missingVoters.length > 0 ? missingVoters.map(p => (
                       <span key={p.id} className="text-xs font-bold bg-[#FF007F]/20 text-[#FF007F] border border-[#FF007F]/30 px-2.5 py-1 rounded-lg">
                         {p.name || "?"}
                       </span>
                     )) : (
                       <span className="text-xs font-bold text-[#00FF66]">Wszyscy zagłosowali!</span>
                     )}
                   </div>
                 </div>
               )}
           </div>
        </div>

        {isHost && (
          <button onClick={actions.end} className="mt-6 mx-auto flex items-center gap-2 text-sm text-[#505060] hover:text-[#FF007F] transition-colors">
            <Trophy size={16}/> {t("end_game")}
          </button>
        )}
      </div>
      
      <div className="w-full z-20 relative">
        <LegalFooter screen="GRA" />
      </div>
    </div>
  );
}